/** UXP-00015 — AI Knowledge Center framework (mirrors pkg/knowledgecenter) */

export const UXP_KC_ID = 'UXP-00015';

export interface CommandCenterKPI {
  label: string;
  value: string;
  change: string;
  positive: boolean;
}

export interface KnowledgeArticleRow {
  type: string;
  title: string;
  version: string;
  status: string;
  updated: string;
}

export interface CourseRow {
  title: string;
  module: string;
  progress: number;
  certified: boolean;
  enrolled: number;
}

export interface TutorSessionRow {
  capability: string;
  question: string;
  answer: string;
}

export interface SOPRow {
  title: string;
  version: string;
  status: string;
  reviewDue: string;
  acknowledged: number;
}

export interface GraphNodeRow {
  entity: string;
  name: string;
  links: number;
}

export interface SearchResultRow {
  mode: string;
  query: string;
  results: number;
  topHit: string;
}

export interface DocumentRow {
  title: string;
  format: string;
  version: string;
  status: string;
  author: string;
}

export const COMMAND_CENTER_METRICS = [
  'Knowledge Health', 'Coverage', 'Freshness', 'AI Training Status',
  'Knowledge Confidence', 'Documentation Health', 'Learning Progress',
];

export const KNOWLEDGE_BASE_TYPES = [
  'Business Rules', 'SOPs', 'Marketplace Policies', 'Brand Standards', 'Finance Procedures',
  'Operations Guides', 'Prompt Library', 'Templates', 'Playbooks', 'Decision Logs',
];

export const LEARNING_FEATURES = [
  'Courses', 'Videos', 'Walkthroughs', 'Certifications', 'Assessments',
  'Learning Paths', 'Progress Tracking',
];

export const TUTOR_CAPABILITIES = [
  'Conversational Learning', 'Role-based Guidance', 'Business Coaching',
  'Marketplace Education', 'Feature Explanations', 'Interactive Q&A',
];

export const SOP_FEATURES = [
  'Version Control', 'Approvals', 'Publishing', 'Assignments',
  'Acknowledgements', 'Review Cycles',
];

export const GRAPH_ENTITY_TYPES = [
  'Processes', 'Departments', 'Products', 'Brands', 'Policies',
  'Workflows', 'AI Agents', 'Business Relationships',
];

export const SEARCH_MODES = [
  'Semantic Search', 'Vector Search', 'Hybrid Search', 'Natural Language Search',
  'Document Search', 'Conversation Search',
];

export const DOCUMENTATION_FEATURES = [
  'Rich Text Editing', 'Markdown', 'Images', 'Videos', 'Flowcharts',
  'Templates', 'Versioning', 'Publishing',
];

export const AI_GENERATION_TYPES = [
  'SOPs', 'FAQs', 'Training Material', 'Release Notes', 'Best Practices',
  'Executive Summaries', 'Policy Documents',
];

export const ORG_MEMORY_TYPES = [
  'Lessons Learned', 'Decision History', 'Project History', 'Launch History',
  'Customer Cases', 'Post Mortems', 'Meeting Outcomes',
];

export const AGENT_TRAINING_FEATURES = [
  'Knowledge Synchronization', 'Policy Updates', 'Prompt Updates',
  'Brand Rule Updates', 'Marketplace Rule Updates',
];

export const CERTIFICATION_TYPES = [
  'Internal Certifications', 'Role Certifications', 'Marketplace Certifications',
  'Knowledge Tests', 'Renewals',
];

export const KNOWLEDGE_ANALYTICS = [
  'Knowledge Usage', 'Training Completion', 'Search Effectiveness',
  'Knowledge Gaps', 'Documentation Coverage', 'Learning ROI',
];

export const COLLABORATION_FEATURES = [
  'Comments', 'Mentions', 'Reviews', 'Assignments', 'Knowledge Ownership', 'Editorial Workflow',
];

export const ACCESSIBILITY_STANDARDS = [
  'WCAG 2.2 AA', 'Keyboard Navigation', 'Screen Reader Support', 'Reduced Motion', 'Responsive Design',
];

export const ARCHITECTURE_STAGES = [
  'Knowledge Command Center', 'Knowledge Base', 'Learning Academy', 'AI Tutor',
  'SOP Management', 'Knowledge Graph', 'Search Engine', 'Documentation Studio',
];

export const LIVE_ROUTES: Record<string, string> = {
  knowledge: '/knowledge',
  developer: '/developer',
  aix: '/platform/aix',
  copilot: '/copilot',
};

export const DEMO_KPIS: CommandCenterKPI[] = [
  { label: 'Knowledge Health', value: '92/100', change: '+3', positive: true },
  { label: 'Coverage', value: '87%', change: '+5%', positive: true },
  { label: 'Freshness', value: '94% current', change: '12 stale', positive: true },
  { label: 'AI Training Status', value: 'Synced', change: '2h ago', positive: true },
  { label: 'Knowledge Confidence', value: '89%', change: '+2%', positive: true },
  { label: 'Documentation Health', value: '91/100', change: '+4', positive: true },
  { label: 'Learning Progress', value: '68% avg', change: '+12%', positive: true },
];

export const DEMO_ARTICLES: KnowledgeArticleRow[] = [
  { type: 'SOPs', title: 'FBA Inbound Shipment SOP', version: 'v3.2', status: 'published', updated: '2 days ago' },
  { type: 'Marketplace Policies', title: 'Amazon Listing Policy Guide', version: 'v2.8', status: 'published', updated: '1 week ago' },
  { type: 'Prompt Library', title: 'Listing Optimizer Prompt Pack', version: 'v1.4', status: 'published', updated: '3 days ago' },
  { type: 'Playbooks', title: 'Prime Day Launch Playbook', version: 'v4.0', status: 'review', updated: 'today' },
  { type: 'Finance Procedures', title: 'Settlement Reconciliation Procedure', version: 'v2.1', status: 'published', updated: '5 days ago' },
  { type: 'Decision Logs', title: 'Q2 Pricing Decision Log', version: 'v1.0', status: 'published', updated: '2 weeks ago' },
];

export const DEMO_COURSES: CourseRow[] = [
  { title: 'Amazon Seller Fundamentals', module: 'Marketplace', progress: 100, certified: true, enrolled: 42 },
  { title: 'PPC Campaign Management', module: 'Advertising', progress: 72, certified: false, enrolled: 28 },
  { title: 'Inventory & Replenishment', module: 'Operations', progress: 45, certified: false, enrolled: 35 },
  { title: 'Financial Intelligence Basics', module: 'Finance', progress: 88, certified: false, enrolled: 18 },
];

export const DEMO_TUTOR: TutorSessionRow[] = [
  { capability: 'Marketplace Education', question: 'What is Buy Box eligibility?', answer: 'Buy Box requires competitive pricing, FBA fulfillment, and strong seller metrics...' },
  { capability: 'Feature Explanations', question: 'How does the Forecast Engine work?', answer: 'The Forecast Engine combines historical sales, seasonality, and inventory signals...' },
  { capability: 'Business Coaching', question: 'When should I increase ad spend?', answer: 'Increase spend when ROAS exceeds 4x and inventory cover exceeds 30 days...' },
];

export const DEMO_SOPS: SOPRow[] = [
  { title: 'Return Processing SOP', version: 'v2.4', status: 'published', reviewDue: 'Sep 2026', acknowledged: 24 },
  { title: 'Catalog Suppression Response', version: 'v1.8', status: 'published', reviewDue: 'Oct 2026', acknowledged: 18 },
  { title: 'Settlement Dispute Escalation', version: 'v3.0', status: 'pending_approval', reviewDue: 'Aug 2026', acknowledged: 0 },
];

export const DEMO_GRAPH: GraphNodeRow[] = [
  { entity: 'Products', name: 'Hub Gen 3', links: 42 },
  { entity: 'Workflows', name: 'Prime Day Listing Prep', links: 18 },
  { entity: 'AI Agents', name: 'Listing Optimizer Agent', links: 24 },
  { entity: 'Policies', name: 'Amazon Listing Policy', links: 56 },
  { entity: 'Brands', name: 'TechHub', links: 128 },
];

export const DEMO_SEARCH: SearchResultRow[] = [
  { mode: 'Semantic Search', query: 'how to handle FBA returns', results: 12, topHit: 'Return Processing SOP v2.4' },
  { mode: 'Natural Language Search', query: 'settlement variance procedure', results: 8, topHit: 'Settlement Reconciliation Procedure' },
  { mode: 'Hybrid Search', query: 'Prime Day checklist', results: 15, topHit: 'Prime Day Launch Playbook v4.0' },
];

export const DEMO_DOCUMENTS: DocumentRow[] = [
  { title: 'Amazon Connect Setup Guide', format: 'markdown', version: 'v2.1', status: 'published', author: 'Platform Team' },
  { title: 'Revenue Intelligence Overview', format: 'rich text', version: 'v1.3', status: 'published', author: 'BI Team' },
  { title: 'Agent Builder Quick Start', format: 'markdown', version: 'v1.0', status: 'draft', author: 'AI Team' },
];

export function buildKnowledgeCenterDashboard() {
  return {
    command_center_metrics: COMMAND_CENTER_METRICS.length,
    knowledge_base_types: KNOWLEDGE_BASE_TYPES.length,
    learning_features: LEARNING_FEATURES.length,
    search_modes: SEARCH_MODES.length,
    documentation_features: DOCUMENTATION_FEATURES.length,
  };
}
